import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { ConfirmationDialog } from "../../../../components/ui/ConfirmationDialog";
import { NotificationSnackbar } from "../../../../components/ui/NotificationSnackbar";
import { logError } from "../../../../utils/error";
import { CONVERSATION_LENS_PACK } from "../../../../utils/terminology";
import { useAuth } from "../../../auth/AuthContext";
import { useNotification } from "../../hooks/useNotification";
import { useAccountAnalysisStore } from "../../store/accountAnalysisStore";
import { AnalysisProfile, LensGroup } from "../../types";
import { LensGroupDialog } from "./LensGroupDialog";
import { ProfileDialog } from "./ProfileDialog";
import { ProfileMembersDialog } from "./ProfileMembersDialog";
import { ProfileTable } from "./ProfileTable";

interface AnalysisProfilesProps {
  onPeopleTabClick?: () => void;
}

export const AnalysisProfiles: React.FC<AnalysisProfilesProps> = ({ onPeopleTabClick }) => {
  const { tenantId } = useAuth();
  const {
    profiles,
    entities,
    lensGroups,
    lenses,
    isLoadingProfiles,
    fetchLensGroups,
    addProfile,
    updateProfile,
    deleteProfile
  } = useAccountAnalysisStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<AnalysisProfile | null>(
    null
  );
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] =
    useState<AnalysisProfile | null>(null);
  const [lensGroupDialogOpen, setLensGroupDialogOpen] = useState(false);
  const [selectedLensGroup, setSelectedLensGroup] = useState<LensGroup | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    open: false,
    title: "",
    message: "",
    onConfirm: async () => {}
  });
  const { notification, showNotification, hideNotification } = useNotification();

  // Ensure external lens data is loaded
  useEffect(() => {
    if (tenantId && lensGroups.length === 0) {
      console.log("AnalysisProfiles: Fetching lens data for tenantId:", tenantId);
      fetchLensGroups(tenantId);
    }
  }, [tenantId, lensGroups.length, fetchLensGroups]);

  // Debug: log lens groups
  console.log("AnalysisProfiles lensGroups:", lensGroups);

  const handleAddProfile = () => {
    setEditingProfile(null);
    setDialogOpen(true);
  };

  const handleEditProfile = (profile: AnalysisProfile) => {
    setEditingProfile(profile);
    setDialogOpen(true);
  };

  const handleSaveProfile = async (profileData: Partial<AnalysisProfile>) => {
    try {
      if (editingProfile) {
        await updateProfile(editingProfile.id, profileData);
      } else {
        await addProfile({
          tenant_id: tenantId || "default-tenant",
          name: profileData.name!,
          central_inbox_email: profileData.central_inbox_email!,
          lens_group_name: profileData.lens_group_name!,
          status: profileData.status || "active"
        });
      }
      setDialogOpen(false);
      setEditingProfile(null);
    } catch (error) {
      logError("Failed to save profile", error);
      showNotification("Failed to save analysis group. Please try again.");
    }
  };

  const handleDeleteProfile = (id: string) => {
    const profile = profiles.find((p) => p.id === id);
    if (!profile) {
      return;
    }

    const memberCount = entities.filter(
      (e) => e.analysis_profile_id === id
    ).length;

    let message = `Are you sure you want to delete "${profile.name}"?`;
    if (memberCount > 0) {
      message += ` This group has ${memberCount} ${
        memberCount === 1 ? "person" : "people"
      }. They will be set to "Unassigned".`;
    }

    setDeleteConfirmation({
      open: true,
      title: "Delete Analysis Group",
      message,
      onConfirm: async () => {
        try {
          // Update entities to unassigned first
          const entitiesToUpdate = entities.filter((e) => e.analysis_profile_id === profile.id);
          await Promise.all(
            entitiesToUpdate.map((entity) =>
              useAccountAnalysisStore
                .getState()
                .updateEntity(entity.id, { analysis_profile_id: null })
            )
          );

          // Then delete the profile
          await deleteProfile(profile.id);
          setDeleteConfirmation(prev => ({ ...prev, open: false }));
        } catch (error) {
          logError("Failed to delete profile", error);
          showNotification("Failed to delete analysis group. Please try again.");
        }
      }
    });
  };

  const handleViewMembers = (profileId: string) => {
    const profile = profiles.find((p) => p.id === profileId);
    setSelectedProfile(profile || null);
    setMembersDialogOpen(true);
  };

  const handleViewLensGroup = (lens_group_name: string | null) => {
    const lensGroup = lens_group_name
      ? lensGroups.find(lg => lg.name === lens_group_name)
      : lensGroups.find(lg => lg.source === "industry");
    setSelectedLensGroup(lensGroup || null);
    setLensGroupDialogOpen(true);
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Analysis Groups
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure {CONVERSATION_LENS_PACK}s and central inbox email addresses for
          specific groups of users
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Note:</strong> People assignments are managed on the People
        tab. Click on people counts below to view which people belong to each
        group.
      </Alert>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProfile}
        >
          Add Group
        </Button>
      </Box>

      <ProfileTable
        profiles={profiles}
        entities={entities}
        lensGroups={lensGroups}
        isLoading={isLoadingProfiles}
        onEditProfile={handleEditProfile}
        onDeleteProfile={handleDeleteProfile}
        onViewMembers={handleViewMembers}
        onViewLensGroup={handleViewLensGroup}
      />

      <ProfileDialog
        open={dialogOpen}
        profile={editingProfile}
        lensGroups={lensGroups}
        profiles={profiles}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveProfile}
      />

      <ProfileMembersDialog
        open={membersDialogOpen}
        profile={selectedProfile}
        entities={entities}
        onClose={() => setMembersDialogOpen(false)}
        onPeopleTabClick={onPeopleTabClick}
      />

      <LensGroupDialog
        open={lensGroupDialogOpen}
        lensGroup={selectedLensGroup}
        lenses={lenses}
        onClose={() => {
          setLensGroupDialogOpen(false);
          setSelectedLensGroup(null);
        }}
      />

      <NotificationSnackbar
        {...notification}
        onClose={hideNotification}
      />

      <ConfirmationDialog
        open={deleteConfirmation.open}
        title={deleteConfirmation.title}
        message={deleteConfirmation.message}
        onConfirm={deleteConfirmation.onConfirm}
        onCancel={() => setDeleteConfirmation(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};
